// ~/composables/useGoogleEvents.client.ts
import type {CartInterface, CartItemInterface} from "~~/server/types/CartInterface";
import type {ProductListInterface} from "~~/server/types/Products";
import type {OrderInterface} from "~~/server/types/OrderInterface";

type GoogleEventSender = (event: string, properties: Record<string, unknown>) => boolean
let eventSender: GoogleEventSender | undefined
let adsPurchaseSender: ((order: OrderInterface) => boolean) | undefined
export function configureGoogleAdsPurchaseSender(sender: (order: OrderInterface) => boolean) { adsPurchaseSender = sender }

export function configureGoogleEventSender(sender: GoogleEventSender) {
    eventSender = sender
}

export function useGoogleEvents() {
    const settings = useSettingsStore()
    const { consent } = useCookieConsent()
    const ready = () => consent.value.decided && consent.value.analytics && !!eventSender
        && !!(settings.settings.ga || settings.settings.gtag)
    const send = (_command: string, event: string, properties: Record<string, unknown>) => eventSender?.(event, properties) ?? false
    const currency = () => settings.settings.currency || 'EUR'

    const toVariant = (options: any) =>
        Object.values(options || {})
            .map((v) => (typeof v === "string" || typeof v === "number" ? String(v) : JSON.stringify(v)))
            .join(", ");

    const cartValue = (cart: CartInterface) =>
        cart.products.reduce((acc, cur) => acc + Number(cur.total ?? 0), 0);

    const viewItem = (product: ProductListInterface) => {
        if (!ready()) return;
        return send("event", "view_item", {
            currency: currency(),
            value: Number(product.price ?? 0),
            items: [
                {
                    item_id: String(product.id),
                    item_name: product.name,
                    item_brand: product.brand ?? undefined,
                    item_category: product.categories_list?.[0]?.name,
                    price: Number(product.price ?? 0),
                },
            ],
        });
    };

    const addToCart = (cartItem: CartItemInterface) => {
        if (!ready()) return;
        return send("event", "add_to_cart", {
            currency: currency(),
            value: Number(cartItem.total ?? 0),
            items: [
                {
                    item_id: String(cartItem.product_id),
                    item_name: cartItem.product.name,
                    item_brand: cartItem.product.brand ?? undefined,
                    item_category: cartItem.product.categories_list?.[0]?.name,
                    item_variant: toVariant(cartItem.options_meta),
                    price: Number(cartItem.price ?? 0),
                    quantity: Number(cartItem.count ?? 0),
                },
            ],
        });
    };

    const removeFromCart = (cartItem: CartItemInterface) => {
        if (!ready()) return;
        return send("event", "remove_from_cart", {
            currency: currency(),
            value: Number(cartItem.total ?? 0),
            items: [
                {
                    item_id: String(cartItem.product_id),
                    item_name: cartItem.product.name,
                    item_brand: cartItem.product.brand ?? undefined,
                    item_category: cartItem.product.categories_list?.[0]?.name,
                    item_variant: toVariant(cartItem.options_meta),
                    price: Number(cartItem.price ?? 0),
                    quantity: Number(cartItem.count ?? 0),
                },
            ],
        });
    };

    const viewCart = (cart: CartInterface) => {
        if (!ready()) return;
        return send("event", "view_cart", {
            currency: currency(),
            value: cartValue(cart),
            items: cart.products.map((cartItem: CartItemInterface) => ({
                item_id: String(cartItem.product_id),
                item_name: cartItem.product.name,
                item_brand: cartItem.product.brand ?? undefined,
                item_category: cartItem.product.categories_list?.[0]?.name,
                item_variant: toVariant(cartItem.options_meta),
                price: Number(cartItem.price ?? 0),
                quantity: Number(cartItem.count ?? 0),
            })),
        });
    };

    const beginCheckout = (cart: CartInterface) => {
        if (!ready()) return;
        return send("event", "begin_checkout", {
            currency: currency(),
            value: cartValue(cart),
            items: cart.products.map((cartItem: CartItemInterface) => ({
                item_id: String(cartItem.product_id),
                item_name: cartItem.product.name,
                item_brand: cartItem.product.brand ?? undefined,
                item_category: cartItem.product.categories_list?.[0]?.name,
                item_variant: toVariant(cartItem.options_meta),
                price: Number(cartItem.price ?? 0),
                quantity: Number(cartItem.count ?? 0),
            })),
        });
    };

    const purchase = (cart: CartInterface | null, order: OrderInterface) => {
        if (!ready()) return;
        const purchase = order.tracking?.purchase;
        return send("event", "purchase", {
            currency: purchase?.currency ?? (order.currency || "EUR"),
            value: Number(purchase?.value ?? order.preorder?.original_total ?? order.total ?? 0),
            transaction_id: order.tracking?.event_id.startsWith('subscription-charge-') ? order.tracking.event_id : String(order.id),
            shipping: Number(purchase?.shipping ?? order.delivery_real ?? 0),
            items: purchase ? purchase.contents.map(item => ({
                item_id: item.id, item_name: item.name, price: item.item_price, quantity: item.quantity,
            })) : (cart?.products ?? []).map((cartItem: CartItemInterface) => ({
                item_id: String(cartItem.product_id),
                item_name: cartItem.product.name,
                item_brand: cartItem.product.brand ?? undefined,
                item_category: cartItem.product.categories_list?.[0]?.name,
                item_variant: toVariant(cartItem.options_meta),
                price: Number(cartItem.price ?? 0),
                quantity: Number(cartItem.count ?? 0),
            })),
        });
    };

    const login = (method: string) => {
        if (!ready()) return;
        return send("event", "login", { method });
    };

    const search = (query: string) => {
        if (!ready()) return;
        return send("event", "search", { search_term: query });
    };

    const addToFavorites = (product: ProductListInterface) => {
        if (!ready()) return;
        return send("event", "add_to_wishlist", {
            currency: currency(),
            value: Number(product.price ?? 0),
            items: [
                {
                    item_id: String(product.id),
                    item_name: product.name,
                    item_brand: product.brand ?? undefined,
                    item_category: product.categories_list?.[0]?.name,
                    price: Number(product.price ?? 0),
                },
            ],
        });
    };

    const callToAction=(product: ProductListInterface)=>{
        if (!ready()) return;
        return send("event", "call_button",{
            event_category: 'Contact',
            items: [
                {
                    item_id: String(product.id),
                    item_name: product.name,
                    item_brand: product.brand ?? undefined,
                    item_category: product.categories_list?.[0]?.name,
                    price: Number(product.price ?? 0),
                },
            ],
        });
    }

    const event = (name: string, properties: Record<string, unknown> = {}) => ready() ? send('event', name, properties) : false

    return {
        event,
        purchaseAds: (order: OrderInterface) => consent.value.decided && consent.value.marketing ? adsPurchaseSender?.(order) ?? false : false,
        viewItem,
        addToCart,
        removeFromCart,
        viewCart,
        addToFavorites,
        beginCheckout,
        purchase,
        login,
        search,
        callToAction
    };
}
